import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';

import Modal from 'modules/views/shared/Modal';
import { WorkerProfileField } from 'types/profileCustomFields';
import { getSkippableTillDisplayTextTranslated } from 'utils/skippableFieldsUtils';

interface SkippableFieldsModalProps {
  isVisible: boolean;
  skippedFields: { field: WorkerProfileField; displayName: string; skippableTill: number }[];
  isLoading?: boolean;
  onContinue: () => void;
  onFillFields?: () => void;
  onClose: () => void;
}

/**
 * Modal that displays a warning when user tries to proceed with empty skippable fields.
 * Gives option to continue anyway or go back to fill the fields.
 */
export const SkippableFieldsModal: React.FC<SkippableFieldsModalProps> = ({
  isVisible,
  skippedFields,
  isLoading = false,
  onContinue,
  onFillFields,
  onClose,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const modalContent = (
    <div className={classes.content}>
      <ul className={classes.fieldsList}>
        {skippedFields.map(({ field, displayName, skippableTill }, index) => (
          <li key={field} className={classes.fieldItem}>
            <div className={classes.bulletIcon} />
            <span className={classes.fieldText}>
              <span className={classes.fieldName}>{displayName}</span>
              <span className={classes.untilText}>
                {' '}
                {t('personalInformation.skippable.isRequiredButSkippableUntil')}{' '}
              </span>
              <span className={classes.skippableTillText}>
                {getSkippableTillDisplayTextTranslated(skippableTill, t)}
              </span>
            </span>
          </li>
        ))}
      </ul>
      <div className={classes.bottomTextContainer}>
        <span className={classes.bottomText}>{t('personalInformation.skippable.provideNowOrLater')}</span>
      </div>
    </div>
  );

  const actionButtons = [
    {
      label: t('common.cancel', 'Cancel'),
      variant: 'outlined' as const,
      onClick: onFillFields || onClose,
      disabled: isLoading,
      testId: 'skippable-fields-cancel-button',
    },
    {
      label: t('common.next', 'Next'),
      variant: 'contained' as const,
      onClick: onContinue,
      isLoading,
      hasEndArrow: true,
      testId: 'skippable-fields-next-button',
    },
  ];

  return (
    <Modal
      isVisible={isVisible}
      title={t('common.warning', 'Warning')}
      Content={modalContent}
      actionButtons={actionButtons}
      onClose={onClose}
    />
  );
};

const useStyles = makeStyles({
  content: {},
  fieldsList: {
    margin: 0,
    padding: '0px 10px',
    listStyle: 'none',
  },
  fieldItem: {
    display: 'flex',
    alignItems: 'flex-start',
    margin: '4px 0',
  },
  bulletIcon: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: '#2B2A29',
    marginTop: '8px',
    marginRight: '8px',
    flexShrink: 0,
  },
  fieldText: {
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '22px',
    color: '#2B2A29',
    margin: 0,
  },
  fieldName: {
    color: '#00346B',
  },
  untilText: {
    color: '#2B2A29',
  },
  skippableTillText: {
    color: '#00346B',
  },
  bottomTextContainer: {
    marginTop: '12px',
  },
  bottomText: {
    color: '#2B2A29',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '22px',
  },
});

export default SkippableFieldsModal;
